import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { KeycloakService } from 'keycloak-angular';

/**
 * Shows the Keycloak login page in an iframe. Only usable when Keycloak is
 * served from the same origin as the app (frame-ancestors 'self' blocks it
 * otherwise) — callers must fall back to keycloakService.login() when not.
 */
@Component({
  selector: 'app-login-modal',
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.css'],
  standalone: true,
  imports: [CommonModule],
})
export class LoginModalComponent implements OnInit {
  loginUrl?: SafeResourceUrl;
  /** App path to land on after login (set via modal data); '' reloads in place. */
  redirectPath = '';

  constructor(
    public modalRef: MdbModalRef<LoginModalComponent>,
    private keycloakService: KeycloakService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    const url = this.keycloakService.getKeycloakInstance().createLoginUrl({
      redirectUri: window.location.origin + '/assets/login-complete.html',
    });
    this.loginUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * The Keycloak theme links to app pages (e.g. /registration). If such a
   * link is missing target="_top", the whole Angular app would load inside
   * this small iframe — detect that (same-origin, so the location is
   * readable) and break out to a full-page navigation instead.
   */
  onFrameLoad(event: Event): void {
    const frame = event.target as HTMLIFrameElement;
    let location: Location | undefined;
    try {
      location = frame.contentWindow?.location;
      if (!location || location.origin !== window.location.origin) {
        return;
      }
    } catch {
      return; // cross-origin frame content — not ours to inspect
    }
    const path = location.pathname;
    if (path.startsWith('/realms/') || path === '/assets/login-complete.html') {
      return; // still inside the Keycloak flow
    }
    this.modalRef.close();
    window.location.href = location.href;
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    if (
      event.origin === window.location.origin &&
      event.data === 'kc-login-success'
    ) {
      this.modalRef.close();
      // The Keycloak SSO cookie is now set; keycloak-js can only init() once,
      // so a full page load lets the check-sso init pick up the session.
      if (this.redirectPath) {
        window.location.href = window.location.origin + this.redirectPath;
      } else {
        window.location.reload();
      }
    }
  }
}
